import ConfirmDeactivation from "./ConfirmDeactivation";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAppSession } from "../context/AppSessionContext";
import { readErrorMessage } from "../lib/http";

export default function DeactivateButton({
  endpoint,
  label,
  returnTo,
  disabled,
  inactive = false,
}: {
  endpoint: string;
  label: string;
  returnTo: string;
  disabled?: boolean;
  inactive?: boolean;
}) {
  const { authorizedFetch } = useAppSession();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const action = inactive ? "Reactivate" : "Deactivate";
  const deactivate = async () => {
    setConfirming(false);
    setBusy(true);
    setError(null);
    try {
      const response = await authorizedFetch(
        inactive ? `${endpoint}/reactivate` : endpoint,
        { method: inactive ? "POST" : "DELETE" },
      );
      if (!response.ok)
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to ${action.toLowerCase()} ${label}.`,
          ),
        );
      toast.success(`${label} ${inactive ? "reactivated" : "deactivated"}.`);
      navigate(returnTo);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : `Failed to ${action.toLowerCase()} ${label}.`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        key={`${action}-${busy}`}
        type="button"
        className="secondaryButton"
        disabled={disabled || busy}
        onClick={() => (inactive ? void deactivate() : setConfirming(true))}
      >
        {busy
          ? inactive
            ? "Reactivating..."
            : "Deactivating..."
          : `${action} ${label.toLowerCase()}`}
      </button>
      <ConfirmDeactivation
        isOpen={confirming}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void deactivate()}
      />
      {error ? (
        <p className="errorMessage" role="alert">
          {error}
        </p>
      ) : null}
    </>
  );
}
